import db from "../../../../database";
import dayjs from "dayjs";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { upsertTableStatus, cancelFBOrder, upsertTableReservation, upsertFBOrder, getFBOrder } from "../../../../services/index";
import { v4 as uuidv4 } from "uuid";
import { generateFormattedKOTs } from "../../../../utils/generateKOTs";
import useAuthStore from "../../../../stores/useAuthStore";
import useCartStore from "../../../../stores/useCartStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { broadcastTableStatus, broadcastOrderUpsert } from "../../../../utils/meshBroadcast";
import { findForeignCartLines, loadTableSessions, withoutCartLines } from "../../../../utils/fbOrderTableOwnership";
import { tracePreparedBill } from "../../../../utils/preparedBillRecovery";

// upsertFbOrder builds its GraphQL mutation via raw string interpolation rather than
// variables, so free-text fields (e.g. cancellation remarks) must be escaped before
// being embedded in a double-quoted string literal — an unescaped newline breaks out
// of the literal and the server rejects the whole mutation with a syntax error.
const escapeGraphQLString = (value) =>
  String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r\n|\r|\n/g, "\\n");

// Reservation `time` arrives here either as a real Date/dayjs-parseable value
// (initial creation, e.g. tables.js's AntD DatePicker field) or as a
// "DD/MM/YYYY, hh:mm A" STRING (every subsequent re-sync — Check In / No Show /
// waitlist edits all round-trip through useWaitlistReservation.js's
// loadReservations(), which formats resTime into that exact string before
// handing it back). Parsing that string with dayjs(time, "DD/MM/YYYY, hh:mm A")
// silently returns "Invalid Date" unless dayjs's customParseFormat plugin has
// been registered — which only happens as a side effect of Dashboard2/index.js
// having been loaded in this session, same trap already documented and worked
// around in useOrderProcessing.js's toISOBusinessDate. A cashier who never
// opens Dashboard2 would have every Check-In/No-Show silently corrupt that
// reservation's date/time on the backend. Parse it manually instead so this
// never depends on an unrelated screen having been visited.
const parseReservationTime = (time) => {
  if (typeof time === "string") {
    const m = /^(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2})\s*(AM|PM)$/i.exec(time.trim());
    if (m) {
      const [, dd, mm, yyyy, hh, min, ampm] = m;
      let hour = parseInt(hh, 10) % 12;
      if (/pm/i.test(ampm)) hour += 12;
      return dayjs(`${yyyy}-${mm}-${dd}T${String(hour).padStart(2, "0")}:${min}:00`);
    }
  }
  return dayjs(time);
};

const SyncData = async (item, type, setLoading, status, IPOrder, cart) => {
  const serverUrl = process.env.REACT_APP_serverUrl;
  const authHeaders = getOAuthHeaders();
  const generateMergeId = () => uuidv4().replace(/-/g, "").toUpperCase();
  let token;
  if (authHeaders) {
    token = authHeaders.access_token;
  }
  const tillData = useAuthStore.getState().tillData;
  const username = useAuthStore.getState().username;
  const salesReps = tillData?.tillAccess?.csBunit?.salesRep || [];
  const matchedRep = salesReps.find((rep) => rep.name === username);
  if (type === "upsertTableStatus") {
    let mergeString = "[]";
    if (item.merge && item.merge.length > 0) {
      mergeString =
        "[" +
        item.merge
          .map(
            (m) => `{
            fbMergeId:"${m.fbMergeId || generateMergeId()}"
            fbMTableId:"${m.fbMTableId || m.tableId}"
            fbMergeReason:"${m.fbMergeReason || m.reason || ""}"
          }`
          )
          .join(",") +
        "]";
    }

    let splitTablesString = "[]";
    if (item.splitTables && item.splitTables.length > 0) {
      splitTablesString =
        "[" +
        item.splitTables
          .map(
            (s) => `{
        splitTable:"${s.splitTable || ""}"
        status:"${s.status || "OPN"}"
        guests:${s.guests || 0}
        guestName:"${s.guestName || ""}"
        guestType:"${s.guestType || ""}"
        ${s.salesRepId ? `salesRepId:"${s.salesRepId}"` : ""}
      }`
          )
          .join(",") +
        "]";
    }

    const salesRepIdToSend = item.waiter || matchedRep?.salesRepresentId || null;

    const paramsInput = {
      query: `mutation {
                  upsertTableStatus(tableStatus: {
                    cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
                    fbtableStatusId: ${item.fbtableStatusId ? `"${item.fbtableStatusId}"` : null}
                    fbTableId: "${item.cwrFbTableId}"
                    fbSectionId: "${item.cwrFbsectionId}"
                    status: "${item.statusType}"
                    guests: ${item.noOfPersons || 0}
                    guestName: "${item.guestName || ""}"
                    guestType: "${item.guestType || ""}"
                    referredBy: "${item.referredBy || ""}"
                    salesRepId: ${salesRepIdToSend ? `"${salesRepIdToSend}"` : null}
                    merge: ${mergeString}
                    splitTables: ${splitTablesString}
                    ${item.splitTable ? `splitTable: "${item.splitTable}"` : ""}
                   }) { 
                status
                message   
                }
                }`,
    };
    // Optimistic write: persist new status to local DB before the API call.
    // If the API fails, the local DB has the correct status and the sync retry
    // will re-attempt the server call on the next syncRestaurantTables run.
    // Per-row update only — a clear()+bulkPut of the whole table erases any
    // concurrent write to OTHER tables (WS handler, fire flow) that lands
    // between the toArray() read and the bulkPut.
    try {
      const updated = {
        statusType: item.statusType,
        color: item.statusType === "OCU" ? "#bc4749" : item.statusType === "RES" ? "#f2e8cf" : "#a7c957",
        tabbleSync: "N",
      };
      if (item.statusType === "OPN") updated.ordered = "N";
      if (item.noOfPersons !== undefined) updated.noOfPersons = item.noOfPersons;
      if (item.guestName !== undefined) updated.guestName = item.guestName;
      if (item.guestType !== undefined) updated.guestType = item.guestType;
      if (item.referredBy !== undefined) updated.referredBy = item.referredBy;
      if (item.waiter !== undefined) updated.waiter = item.waiter;
      if (item.merge !== undefined) updated.merge = item.merge;
      if (item.splitTables !== undefined) updated.splitTables = item.splitTables;
      await db.tableData.update(item.cwrFbTableId, updated);
      broadcastTableStatus(item);
    } catch (dbErr) {
    }

    try {
      const response = await upsertTableStatus(paramsInput);
      const status = response?.data?.data?.upsertTableStatus?.status;

      if (status === "200") {
        await db.tableData.update(item.cwrFbTableId, { tabbleSync: "Y" });
        return { success: true };
      }

      console.warn("SyncData upsertTableStatus status not 200:", status);
      return { success: false };
    } catch (err) {
      console.error("SyncData upsertTableStatus error:", err);
      return { success: false, error: err };
    }
  } else if (type === "cancelItem") {
    const cancelItemsData = {
      query: `mutation {
                cancelFBOrder(fbOrder: {
                  fbOrderId:"${item.fbOrderId}"
                  lines:[${item.cancelItems}]
                 }) { 
              status
              message   
              }
              }`,
    };
    await cancelFBOrder(cancelItemsData);
  } else if (type === "reservation") {
    const fbTableIdValue = item.cwrFbTableId || item.tableId || null;
    const fbSectionIdValue = item.cwrFbsectionId || item.sectionId || null;
    const paramsInput = {
      query: `mutation {
              upsertTableReservation(tableReservation: {
                cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
                fbTableReservationId: "${item.uniqueId ? item.uniqueId : item.id}"
                fbTableId: ${fbTableIdValue ? `"${fbTableIdValue}"` : null}
                fbSectionId: ${fbSectionIdValue ? `"${fbSectionIdValue}"` : null}
                customerName: "${item.name === undefined ? "" : item.name}"
                noOfPeople: "${item.noOfPersons ? item.noOfPersons : item.guests}"
                ${item.mobile || item.phone ? `contactNo: "${item.mobile || item.phone}"` : ""},
                ${item.guestType ? `guestType: "${item.guestType}"` : ""},
                ${item.referredBy ? `referredBy: "${item.referredBy}"` : ""},
                ${item.status ? `status: "${item.status}"` : ""},
                 resDate: "${parseReservationTime(item.time).format("YYYY-MM-DD")}"

    resTime: "${parseReservationTime(item.time).format("YYYY-MM-DD HH:mm:ss")}"
               }) { 
            status
            message   
            }
            }`,
    };
    await upsertTableReservation(paramsInput);
  } else if (type === "upsertFbOrder") {
    // Prefer the explicitly-passed cart, then the order's own persisted cart.
    // The live store cart is the LAST resort: by the time SyncData runs the
    // cashier may have opened a different table, and snapshotting that cart
    // into this order's meta contaminates it with another table's items.
    const singleOrder = Array.isArray(item) ? item[0] : item;
    const cartDetails = cart || singleOrder?.cart || useCartStore.getState().cart;
    const tableName = useRestaurantStore.getState().tableName;
    const dineIn = useRestaurantStore.getState().dineIn;

    const fbOrderMetaId = uuidv4().replace(/-/g, "").toUpperCase();
    const fbOrders = JSON.parse(localStorage.getItem("getFBOrderData") || "[]");

    let tempResponse = null;

    // Use item directly — no DB query needed
    const orders = Array.isArray(item) ? item : [item];
    // BUG-2636: null when the read fails — the table check is then skipped (behaviour before it).
    const tableSessions = orders.length > 0 ? await loadTableSessions() : null;

    if (orders.length > 0) {
      for (let i = 0; i < orders.length; i++) {
        let o = orders[i];
        // BUG-2636: the snapshot sent for this order must be this table's cart. A cart of another
        // table (the live cart after the waiter moved on) gives way to the order's own cart, and
        // lines another table's open session owns are dropped from the snapshot, the lines and the KOTs.
        const orderTableId = o.cwrFbTableId || o.fbTableId;
        let orderCart = cartDetails;
        if (orderTableId && cartDetails?.tableId && String(cartDetails.tableId) !== String(orderTableId) && String(o.cart?.tableId) === String(orderTableId)) {
          orderCart = o.cart;
        }
        const foreign = tableSessions
          ? findForeignCartLines({ items: [...(orderCart?.items || []), ...(Array.isArray(o.lines) ? o.lines : [])], tableId: orderTableId, fbOrderId: o.fbOrderId, sessions: tableSessions })
          : [];
        const usedOrderCart = orderCart !== cartDetails;
        if (usedOrderCart || foreign.length > 0) {
          const foreignIds = new Set(foreign.map((f) => f.item.fbOrderLineId));
          orderCart = withoutCartLines(orderCart, foreignIds);
          if (foreignIds.size > 0 && Array.isArray(o.lines)) o = { ...o, lines: o.lines.filter((l) => !foreignIds.has(l?.fbOrderLineId)) };
          tracePreparedBill("fb-order-foreign-lines-dropped", {
            fbOrderId: o.fbOrderId,
            tableId: orderTableId,
            cartTableId: cartDetails?.tableId,
            usedOrderCart,
            dropped: foreign.map((f) => ({ fbOrderLineId: f.item.fbOrderLineId, name: f.item.name || f.item.description, ownerTableId: f.tableId, ownerFbOrderId: f.fbOrderId })),
          });
        }
        // BUG-2673: a snapshot whose cart is still another table's (the order's own cart was
        // overwritten too, so the swap above could not help) is not sent: the server keeps this
        // order's last cartDetails instead of showing that table's items on reopen.
        const foreignSnapshot = !!(orderTableId && orderCart?.tableId && String(orderCart.tableId) !== String(orderTableId));
        if (foreignSnapshot) {
          tracePreparedBill("fb-order-foreign-cart-snapshot-skipped", { fbOrderId: o.fbOrderId, tableId: orderTableId, cartTableId: orderCart.tableId, sOrderID: orderCart.sOrderID });
        }
        // tableNo: the opened table's name only when it is this order's table.
        const openTableMatches = !tableName?.cwrFbTableId || String(tableName.cwrFbTableId) === String(orderTableId);
        const tableNo = openTableMatches ? tableName?.name || "" : orderCart?.tableNo || "";
        const cartItems = { ...orderCart, tableNo };
        const cartMetaValue = JSON.stringify(JSON.stringify(cartItems));

          let metaString = "[]";
          // Kept in JS-object form (not just the GraphQL string above) so it can be
          // attached to `o` and forwarded to broadcastOrderUpsert below — mesh peers
          // have no server round-trip to fetch this cartDetails snapshot from, so if it
          // isn't handed to them directly here, ORDER_UPSERT arrives over mesh with an
          // empty meta and a waiter's already-open table never sees the cashier's
          // cancel/edit (useWebSocketHandler.js's safeParseWsData yields {} and the
          // item merge has nothing to reconcile against).
          let orderMeta = [];
          if (IPOrder && o.isCanceled !== "Y" && !foreignSnapshot) {
            metaString = `[
            {
              fbOrderMetaId: "${fbOrderMetaId}"
              key: "cartDetails"
              value: ${cartMetaValue}
            }
          ]`;
            orderMeta = [{ fbOrderMetaId, key: "cartDetails", value: JSON.stringify(cartItems) }];
          }

          const filteredOrders = Array.isArray(fbOrders)
            ? fbOrders.filter(
              (fo) => fo.fbTableId === o.cwrFbTableId || fo.fbTableId === o.fbTableId
            )
            : [];

          let selectedProductIds = [];
          if (Array.isArray(o.lines)) {
            selectedProductIds = o.lines
              .filter(
                (line) =>
                  line &&
                  line.fbOrderLineId &&
                  line.fbOrderLineId !== "undefined" &&
                  line.mPoductId &&
                  line.mPoductId !== "undefined" &&
                  line.csUomId &&
                  line.csUomId !== "undefined" &&
                  line.csTaxId &&
                  line.csTaxId !== "undefined"
              )
              .map((line) => line.lineId);
          }

          let mergeString = "[]";
          if (o.mergeTables && o.mergeTables.length > 0) {
            mergeString =
              "[" +
              o.mergeTables
                .map(
                  (m) => `{
                  fbMergeId:"${generateMergeId()}"
                  fbMergeReason:"${m.reason}"
                  fbMTableId:"${m.tableId}"
                }`
                )
                .join(",") +
              "]";
          }

          let linesString = "[]";
          if (Array.isArray(o.lines) && o.lines.length > 0 && typeof o.lines[0] === "object") {
            const formattedLines = o.lines.map((line) => {
              const lineMetaString = line.meta
                ? line.meta
                  .map(
                    (m) => `{
                      fbOrderLineMetaId:"${m.fbOrderLineMetaId || ""}"
                      key:"${m.key}"
                      value:${JSON.stringify(m.value)}
                    }`
                  )
                  .join(",")
                : "";

              return `{
              fbOrderId: "${o.fbOrderId}"
              fbOrderLineId: "${line.fbOrderLineId}"
              mPoductId: "${line.mPoductId}"
              mBatchId: ${line.mBatchId ? `"${line.mBatchId}"` : null}
              description: "${line.description || ""}"
              csUomId: "${line.csUomId}"
              csTaxId: "${line.csTaxId}"
              discount:${line.discount ?? 0}
              line:${line.line ?? 0}
              qty:${line.qty ?? 0}
              unitPrice:${line.unitPrice ?? 0}
              listPrice:${line.listPrice ?? 0}
              lineNet:${line.lineNet ?? 0}
              lineTax:${line.lineTax ?? 0}
              lineGross:${line.lineGross ?? 0}
              sOrderLineId:${line.sOrderLineId ? `"${line.sOrderLineId}"` : null}
              isOrdered:"${line.isOrdered || "N"}"
              isCanceled:"${line.isCanceled || "N"}"
              remarks: "${escapeGraphQLString(line.remarks)}"
              grossStd:${line.grossStd || 0}
              grossUnit:${line.grossUnit || 0}
              lineStd:${line.lineStd || 0}
              netList:${line.netList ?? 0}
              netStd:${line.netStd ?? 0}
              netUnit:${line.netUnit || 0}
              unitTax:${line.unitTax ?? 0}
              meta:[${lineMetaString}]
            }`;
            });

            linesString = `[${formattedLines.join(",")}]`;
          }

          // skipKots lets a caller re-sync an order's cartDetails snapshot (e.g. after a
          // manual discount) without re-firing KOTs. Non-IP orders never fire KOTs either.
          const skipKots = o.fbOrderStatus !== "IP" || o.skipKots === true;
          const formattedKots = skipKots ? [] : generateFormattedKOTs(orderCart, tillData, selectedProductIds);
          const kotsSection = skipKots ? "" : `kots:[${formattedKots.join(",")}]`;

          const paramsInput = {
            query: `mutation {
            upsertFBOrder(order: {
              cSBunitID: "${tillData.tillAccess.csBunit.csBunitId}"
              fbOrderId: "${o.fbOrderId}"
              fbTableId: "${o.cwrFbTableId}"
              fbSectionId: ${o.cwrFbsectionId ? `"${o.cwrFbsectionId}"` : null}
              sOrderId: null
              isCanceled: "${o.isCanceled || "N"}"
              orderNo: null
              date: "${dayjs().format("YYYY-MM-DD HH:mm:ss")}"
              status: "${o.fbOrderStatus}"
              merge: ${mergeString}
              meta: ${metaString}
              lines: ${linesString}
              ${kotsSection}
            }) {
              status
              message
              documentNo
            }
          }`,
          };

          const response = await upsertFBOrder(paramsInput);

          if (response) {
            useRestaurantStore.getState().setCancelKOTRemarks(null);
            broadcastOrderUpsert({ ...o, meta: orderMeta });
            const documentNo = response?.data?.data?.upsertFBOrder?.documentNo;
            if (documentNo) {
              localStorage.setItem("orderId", documentNo);
            }
            tempResponse = response;
          }
        }
    }

    return tempResponse;
  } else if (type === "getFbOrder") {
    const paramsInput = {
      query: `query {
        getFBOrder(
          tableId: "${item.tableId}"
          status: "${item.status}"
        ) {
          fbOrderId
          cSBunitID
          isactive
          sOrderId
          fbTableId
          orderNo
          fbSectionId
          date
          guests
          guestType
          salesRepId
          guestName
          referredBy
          status
          isCanceled
          discAmount
          fbSplitTable
          merge {
            fbMergeId
            fbMergeReason
            fbTableId
          }
          lines {
            fbOrderLineId
            line
            mPoductId
            mBatchId
            description
            csUomId
            qty
            unitPrice
            listPrice
            csTaxId
            lineTax
            lineGross
            isOrdered
            sOrderLineId
            isCanceled
            discount
            mPricingRuleId
            name
            value
            grossStd
            grossUnit
            lineStd
            netList
            netStd
            netUnit
            unitTax
            meta {
              fbOrderLineMetaId
              key
              value
            }
          }
          meta {
            fbOrderMetaId
            key
            value
          }
        }
      }`,
    };

    return await getFBOrder(paramsInput);
  }
};

export { SyncData };
